import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/useAuth';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type {
  ChatSession,
  ChatSessionEntity,
} from '../../../types/chatSession';
import type { ChatSessionsContextValue } from '../../../types/chatSessionsContext';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { PLATFORM_CHAT_SESSIONS_SEARCH_DEBOUNCE_MS } from '../platformChatConstants';
import { usePlatformChatPinnedSessionsInfiniteQuery } from '../queries/usePlatformChatPinnedSessionsInfiniteQuery';
import { usePlatformChatUnpinnedSessionsInfiniteQuery } from '../queries/usePlatformChatUnpinnedSessionsInfiniteQuery';
import { ChatSessionsContext } from './chatSessionsContext';
import { mapPlatformSessionsToChatSessions } from './chatSessionListMapping';
import { useChatSessionActions } from './useChatSessionActions';

function resolveActivePlatformChatSessionId(
  pathname: string,
): string | undefined {
  const match = pathname.match(/^\/platform-chat\/([^/]+)/);
  const sessionId = match?.[1];
  return sessionId != null && sessionId.length > 0 ? sessionId : undefined;
}

export function ChatSessionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const {
    isUserConfigReady,
    platformChatLastSeenMessageIds,
    seedPlatformChatLastSeenMessageIds,
  } = useUserConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(
    searchQuery,
    PLATFORM_CHAT_SESSIONS_SEARCH_DEBOUNCE_MS,
  );
  const activeSessionId = resolveActivePlatformChatSessionId(pathname);

  const pinnedQuery =
    usePlatformChatPinnedSessionsInfiniteQuery(debouncedSearch);
  const unpinnedQuery =
    usePlatformChatUnpinnedSessionsInfiniteQuery(debouncedSearch);

  const pinnedItems = useMemo(
    () => pinnedQuery.data?.pages.flatMap((page) => page) ?? [],
    [pinnedQuery.data?.pages],
  );
  const unpinnedItems = useMemo(
    () => unpinnedQuery.data?.pages.flatMap((page) => page) ?? [],
    [unpinnedQuery.data?.pages],
  );

  useEffect(() => {
    if (!isUserConfigReady) {
      return;
    }

    const seeds: Record<string, string> = {};

    for (const item of [...pinnedItems, ...unpinnedItems]) {
      if (item.participantCount <= 1) {
        continue;
      }

      const messageId = item.mostRecentMessage?.id;
      if (typeof messageId !== 'string' || messageId.length === 0) {
        continue;
      }

      const sessionId = item.session.id;
      if (platformChatLastSeenMessageIds[sessionId] != null) {
        continue;
      }

      seeds[sessionId] = messageId;
    }

    if (Object.keys(seeds).length === 0) {
      return;
    }

    seedPlatformChatLastSeenMessageIds(seeds);
  }, [
    isUserConfigReady,
    pinnedItems,
    platformChatLastSeenMessageIds,
    seedPlatformChatLastSeenMessageIds,
    unpinnedItems,
  ]);

  const mapOptions = useMemo(
    () => ({
      currentUserId: user?.id,
      activeSessionId,
      lastSeenMessageIds: platformChatLastSeenMessageIds,
    }),
    [activeSessionId, platformChatLastSeenMessageIds, user?.id],
  );

  const sessionsById = useMemo(() => {
    const map = new Map<string, ChatSessionEntity>();
    for (const item of pinnedItems) {
      map.set(item.session.id, item.session);
    }
    for (const item of unpinnedItems) {
      map.set(item.session.id, item.session);
    }
    return map;
  }, [pinnedItems, unpinnedItems]);

  const pinnedSessions = useMemo(
    () => mapPlatformSessionsToChatSessions(pinnedItems, mapOptions),
    [mapOptions, pinnedItems],
  );
  const unpinnedSessions = useMemo(
    () => mapPlatformSessionsToChatSessions(unpinnedItems, mapOptions),
    [mapOptions, unpinnedItems],
  );

  const sessions = useMemo<ChatSession[]>(
    () => [...pinnedSessions, ...unpinnedSessions],
    [pinnedSessions, unpinnedSessions],
  );

  const actions = useChatSessionActions({
    currentUserId: user?.id,
    sessions,
    sessionsById,
  });

  const fetchNextPinnedPage = useCallbackStable(
    pinnedQuery.hasNextPage,
    pinnedQuery.isFetchingNextPage,
    pinnedQuery.fetchNextPage,
  );
  const fetchNextUnpinnedPage = useCallbackStable(
    unpinnedQuery.hasNextPage,
    unpinnedQuery.isFetchingNextPage,
    unpinnedQuery.fetchNextPage,
  );

  const isLoading =
    (pinnedQuery.isLoading || unpinnedQuery.isLoading) && sessions.length === 0;

  const value = useMemo<ChatSessionsContextValue>(
    () => ({
      isAvailable: true,
      isLoading,
      searchQuery,
      setSearchQuery,
      pinnedSessions,
      unpinnedSessions,
      sessions,
      hasNextPinnedPage: pinnedQuery.hasNextPage ?? false,
      hasNextUnpinnedPage: unpinnedQuery.hasNextPage ?? false,
      isFetchingNextPinnedPage: pinnedQuery.isFetchingNextPage,
      isFetchingNextUnpinnedPage: unpinnedQuery.isFetchingNextPage,
      fetchNextPinnedPage,
      fetchNextUnpinnedPage,
      ...actions,
    }),
    [
      actions,
      fetchNextPinnedPage,
      fetchNextUnpinnedPage,
      isLoading,
      pinnedQuery.hasNextPage,
      pinnedQuery.isFetchingNextPage,
      pinnedSessions,
      searchQuery,
      sessions,
      unpinnedQuery.hasNextPage,
      unpinnedQuery.isFetchingNextPage,
      unpinnedSessions,
    ],
  );

  return (
    <ChatSessionsContext.Provider value={value}>
      {children}
    </ChatSessionsContext.Provider>
  );
}

function useCallbackStable(
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>,
) {
  return useMemo(() => {
    return () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
}
